/**
 * Framework-agnostic store tracking which GeoJSON layers currently have a pending fetch.
 * Layers push their loading state the instant it changes (see MapboxGeoJSONLayer.updateSource /
 * removeLayer); consumers subscribe via useSyncExternalStore instead of polling.
 */

type Listener = () => void;

const stateById = new Map<number, boolean>();
const listeners = new Set<Listener>();

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

export function reportLayerLoading(layerId: number, isLoading: boolean): void {
  if (stateById.get(layerId) === isLoading) {
    return;
  }
  if (isLoading) {
    stateById.set(layerId, true);
  } else {
    stateById.delete(layerId);
  }
  notify();
}

export function clearLayerLoading(layerId: number): void {
  const hadEntry = stateById.has(layerId);
  stateById.delete(layerId);
  if (hadEntry) {
    notify();
  }
}

export function isLayerLoading(layerId: number): boolean {
  return stateById.get(layerId) ?? false;
}

export function subscribeLayerLoading(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
